import { CSSProperties, FC, RefObject, UIEvent, useCallback, useLayoutEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './ScrollArea.module.css';
import { useScrollPosition } from './hooks/useScrollPosition';
import { useAutosize } from './hooks/useAutosize';
import { ScrollAreaProps, ScrollAreaAutosizeProps, ScrollAreaType, OffsetScrollbars } from './ScrollArea.types';

interface ScrollAreaCSSProperties extends CSSProperties {
  '--scrollarea-scrollbar-size'?: string;
}

const SCROLLBAR_AXIS_CLASSES: Record<string, string> = {
  xy: styles.scrollbarsXY,
  x: styles.scrollbarsX,
  y: styles.scrollbarsY,
  false: styles.scrollbarsNone,
};

const TYPE_CLASSES: Partial<Record<ScrollAreaType, string>> = {
  always: styles.typeAlways,
  never: styles.typeNever,
};

const getOffsetClass = (offset?: OffsetScrollbars): string | undefined => {
  if (offset === true) return styles.offsetBoth;
  if (offset === 'x') return styles.offsetX;
  if (offset === 'y') return styles.offsetY;
};

interface ScrollAreaBaseProps extends ScrollAreaProps {
  ref?: RefObject<HTMLDivElement | null>;
}

const ScrollAreaBase: FC<ScrollAreaBaseProps> = ({
  children,
  'as': Root = 'div',
  'viewportAs': Component = 'div',
  height,
  maxHeight,
  width,
  type = 'auto',
  scrollbars = 'xy',
  scrollbarSize = '0.4rem',
  offsetScrollbars,
  overscrollBehavior,
  startScrollPosition,
  viewportRef,
  viewportProps,
  onScrollPositionChange,
  onTopReached,
  onBottomReached,
  onLeftReached,
  onRightReached,
  className,
  classNames,
  ref,
  'data-testid': dataTestId = 'scroll-area',
}) => {
  const internalViewportRef = useRef<HTMLElement>(null);
  const initialScrollPositionRef = useRef(startScrollPosition);

  const viewportRefCallback = useCallback(
    (node: HTMLElement | null) => {
      internalViewportRef.current = node;
      if (viewportRef) {
        viewportRef.current = node;
      }
    },
    [viewportRef],
  );

  const { handleScroll: handleScrollPosition } = useScrollPosition({
    onScrollPositionChange,
    onTopReached,
    onBottomReached,
    onLeftReached,
    onRightReached,
  });

  useLayoutEffect(() => {
    const viewport = internalViewportRef.current;
    const position = initialScrollPositionRef.current;
    if (!viewport || !position) return;

    viewport.scrollTo({
      top: position.y,
      left: position.x,
    });
  }, []);

  const scrollAxisKey = scrollbars === false ? 'false' : scrollbars;
  const axisClass = SCROLLBAR_AXIS_CLASSES[scrollAxisKey];
  const typeClass = TYPE_CLASSES[type];
  const offsetClass = getOffsetClass(offsetScrollbars);

  const rootStyle: ScrollAreaCSSProperties = {
    'height': height,
    'maxHeight': maxHeight,
    'width': width,
    '--scrollarea-scrollbar-size': scrollbarSize,
  };

  const viewportStyle: CSSProperties = {
    overscrollBehavior,
    ...viewportProps?.style,
  };

  return (
    <Root ref={ref} className={cx(styles.root, className)} style={rootStyle} data-testid={dataTestId}>
      <Component
        ref={viewportRefCallback}
        {...viewportProps}
        className={cx(
          styles.viewport,
          axisClass,
          typeClass,
          offsetClass,
          classNames?.viewport,
          viewportProps?.className,
        )}
        style={viewportStyle}
        onScroll={(e: UIEvent<HTMLDivElement>) => {
          handleScrollPosition(e);
          viewportProps?.onScroll?.(e);
        }}
        data-testid={`${dataTestId}-viewport`}
      >
        {children}
      </Component>
    </Root>
  );
};

interface ScrollAreaAutosizeBaseProps extends ScrollAreaAutosizeProps {
  ref?: RefObject<HTMLDivElement | null>;
}

const ScrollAreaAutosize: FC<ScrollAreaAutosizeBaseProps> = ({
  maxHeight,
  onOverflowChange,
  children,
  ref,
  ...scrollAreaProps
}) => {
  const { contentRef, isOverflowing } = useAutosize({ maxHeight, onOverflowChange });

  const resolvedHeight = isOverflowing ? maxHeight : undefined;

  return (
    <div className={styles.autosizeRoot} data-testid={`${scrollAreaProps['data-testid'] ?? 'scroll-area'}-autosize`}>
      <ScrollAreaBase ref={ref} {...scrollAreaProps} height={resolvedHeight}>
        <div ref={contentRef}>{children}</div>
      </ScrollAreaBase>
    </div>
  );
};

const ScrollArea = Object.assign(ScrollAreaBase, {
  Autosize: ScrollAreaAutosize,
});

export default ScrollArea;
export type { ScrollAreaProps, ScrollAreaAutosizeProps };
